import { useCallback, useEffect, useState } from 'react'
import {
  usePutApiV1DevicesMutation,
  useLazyGetApiV1DevicesVapidPublicKeyQuery,
  useDeleteApiV1DevicesByDeviceIdMutation,
} from '../../../api'
import {
  bufferToBase64Url,
  urlBase64ToUint8Array,
  extractFcmToken,
} from '../../helpers/utils'
import { getDeviceId, clearDeviceId } from '../../helpers/deviceId'
import { logger } from 'helpers/logger'

export type PushNotificationState = {
  isSupported: boolean
  permission: NotificationPermission | null
  isRegistered: boolean
  isLoading: boolean
  error: string | null
}

export type UsePushNotificationsReturn = PushNotificationState & {
  register: () => Promise<boolean>
  unregister: () => Promise<void>
}

const SERVICE_WORKER_PATH = '/sw/sw.js'

const isPushSupported = (): boolean => {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: isPushSupported(),
    permission: isPushSupported() ? Notification.permission : null,
    isRegistered: false,
    isLoading: false,
    error: null,
  })

  const [registerDevice] = usePutApiV1DevicesMutation()
  const [fetchVapidKey] = useLazyGetApiV1DevicesVapidPublicKeyQuery()
  const [deleteDevice] = useDeleteApiV1DevicesByDeviceIdMutation()

  useEffect(() => {
    if (!state.isSupported) {
      return
    }

    const updatePermission = () => {
      setState((prev) => ({
        ...prev,
        permission: Notification.permission,
      }))
    }

    updatePermission()

    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          status.onchange = updatePermission
        })
        .catch(() => logger.warn('Failed to query notification permission'))
    }
  }, [state.isSupported])

  const register = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      setState((prev) => ({
        ...prev,
        error: 'Push notifications are not supported in this browser',
      }))
      return false
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const registration = await navigator.serviceWorker.register(
        SERVICE_WORKER_PATH,
        { scope: '/', type: 'module' },
      )

      await navigator.serviceWorker.ready

      const permission = await Notification.requestPermission()

      setState((prev) => ({ ...prev, permission }))

      if (permission !== 'granted') {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Notification permission denied',
        }))
        return false
      }

      const vapidResponse = await fetchVapidKey().unwrap()
      const applicationServerKey = urlBase64ToUint8Array(
        vapidResponse.publicKey,
      )

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer,
      })

      const p256dh = subscription.getKey('p256dh')
      const auth = subscription.getKey('auth')

      if (!p256dh || !auth) {
        throw new Error('Failed to get subscription keys')
      }

      await registerDevice({
        deviceRegistrationRequest: {
          deviceId: getDeviceId(),
          fcmToken: extractFcmToken(subscription.endpoint),
          endpoint: subscription.endpoint,
          keys: {
            p256dh: bufferToBase64Url(p256dh),
            auth: bufferToBase64Url(auth),
          },
          platform: 'web',
          device: navigator.userAgent,
        },
      }).unwrap()

      setState((prev) => ({
        ...prev,
        isRegistered: true,
        isLoading: false,
      }))

      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed'
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }))
      return false
    }
  }, [state.isSupported, fetchVapidKey, registerDevice])

  const unregister = useCallback(async (): Promise<void> => {
    try {
      const deviceId = getDeviceId()
      if (deviceId) {
        await deleteDevice({ deviceId }).unwrap()
      }

      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await subscription.unsubscribe()
        }
      }

      clearDeviceId()

      setState((prev) => ({
        ...prev,
        isRegistered: false,
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unregister failed'
      setState((prev) => ({
        ...prev,
        error: message,
      }))
    }
  }, [deleteDevice])

  return {
    ...state,
    register,
    unregister,
  }
}
